import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin, Mail, Phone, Star, Quote, ChevronLeft, ChevronRight, Clock, Instagram, Youtube } from "lucide-react";
import { BRAND, IMG } from "../lib/site";
import { PageHero } from "../components/PageHero";
import { Reveal, SectionHeading } from "../components/Reveal";
import { LeadForm } from "../components/LeadForm";

const REVIEWS = [
  { name: "Rohit & Neha Agarwal", area: "Bought 3 BHK — Noida Extension", text: "Estate Paradise negotiated ₹4 lakh below the quoted resale price and caught a title discrepancy our own lawyer missed. Ten years of experience shows.", stars: 5 },
  { name: "Priyanka Srivastava", area: "Invested in pre-launch — Siddharth Vihar", text: "They explained floor-rise, payment plans and exit options like family, not salesmen. My unit appreciated 18% before possession even began.", stars: 5 },
  { name: "Col. A. K. Malhotra (Retd.)", area: "Rental — Gaur City 2", text: "Furnished flat, agreement and police verification done in three days flat. The most professional brokerage I have dealt with in NCR.", stars: 5 },
  { name: "Sandeep Yadav", area: "Bought 2 BHK — Wave City", text: "Transparent to the rupee. Every receipt, every charge in writing. I have already sent two colleagues to them.", stars: 5 },
  { name: "Meenakshi Bhardwaj", area: "Resale sale — Indirapuram", text: "Sold my flat 6% above my expectation in under five weeks. Their buyer screening saved me endless wasted weekends.", stars: 4 },
];

const Stars = ({ n }) => (
  <div className="flex gap-1" aria-label={`${n} star review`}>
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`h-4 w-4 ${i < n ? "fill-gold-500 text-gold-500" : "text-navy-900/20"}`} />
    ))}
  </div>
);

const Testimonials = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % REVIEWS.length), 5200);
    return () => clearInterval(t);
  }, [paused]);

  const review = REVIEWS[index];

  return (
    <section className="relative overflow-hidden bg-navy-900 py-24 sm:py-28" data-testid="testimonials-section">
      <Quote className="pointer-events-none absolute -top-8 right-10 h-48 w-48 text-white/5" aria-hidden="true" />
      <div className="relative mx-auto max-w-5xl px-6 lg:px-10">
        <SectionHeading
          dark
          align="center"
          overline="Google Reviews — 4.9 / 5.0"
          title={<>Families who found <span className="italic text-gold-400">home with us</span></>}
        />
        <div
          className="relative mt-14 min-h-[260px] sm:min-h-[220px]"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="testimonials-carousel"
        >
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={index}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="text-center"
            >
              <div className="flex justify-center"><Stars n={review.stars} /></div>
              <p className="mx-auto mt-6 max-w-3xl font-serif text-xl font-light italic leading-relaxed text-white/90 sm:text-2xl">
                "{review.text}"
              </p>
              <footer className="mt-7">
                <p className="text-sm font-bold uppercase tracking-[0.18em] text-gold-400">{review.name}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.16em] text-white/45">{review.area}</p>
              </footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>
        <div className="mt-10 flex items-center justify-center gap-5">
          <button
            data-testid="testimonial-prev-btn"
            onClick={() => setIndex((index - 1 + REVIEWS.length) % REVIEWS.length)}
            aria-label="Previous review"
            className="flex h-11 w-11 items-center justify-center border border-white/20 text-white/70 transition-colors duration-300 hover:border-gold-500 hover:text-gold-400"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex gap-2">
            {REVIEWS.map((_, i) => (
              <button
                key={i}
                data-testid={`testimonial-dot-${i}`}
                onClick={() => setIndex(i)}
                aria-label={`Go to review ${i + 1}`}
                className={`h-1.5 transition-[width,background-color] duration-400 ${i === index ? "w-8 bg-gold-500" : "w-2.5 bg-white/25 hover:bg-white/50"}`}
              />
            ))}
          </div>
          <button
            data-testid="testimonial-next-btn"
            onClick={() => setIndex((index + 1) % REVIEWS.length)}
            aria-label="Next review"
            className="flex h-11 w-11 items-center justify-center border border-white/20 text-white/70 transition-colors duration-300 hover:border-gold-500 hover:text-gold-400"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

const Story = () => (
  <section className="bg-white py-24 sm:py-32" data-testid="story-section">
    <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-2 lg:px-10">
      <Reveal>
        <div className="relative">
          <div className="absolute -left-5 -top-5 h-full w-full border border-gold-500/60" aria-hidden="true" />
          <img src={IMG.trust} alt="Estate Paradise partnership handshake" className="relative aspect-[4/5] w-full object-cover sm:aspect-[5/5]" />
          <div className="absolute -bottom-7 -right-4 bg-navy-900 px-7 py-5 shadow-[0_24px_50px_rgb(13,27,62,0.3)] sm:-right-7">
            <p className="font-serif text-4xl font-light text-gold-400">10+</p>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.22em] text-white/60">Years of Trust</p>
          </div>
        </div>
      </Reveal>
      <div>
        <SectionHeading
          overline="Our Story"
          title={<>A decade of trust <span className="italic text-gold-600">&amp; transparency</span></>}
        />
        <Reveal delay={0.15}>
          <div className="mt-7 space-y-5 text-base leading-relaxed text-gray-600">
            <p>
              Estate Paradise began in 2015 at a single desk in Gaur City with one rule: never show a client a property
              we wouldn't buy ourselves. Ten years on, that rule has helped over a thousand families buy, sell and rent
              across Ghaziabad and Noida Extension.
            </p>
            <p>
              We are brokers the old-fashioned way — physically verifying every listing, negotiating like it's our own
              money, and putting every charge in writing before you commit. That's why our Google rating sits at 4.9
              stars, and why most of our business arrives as referrals.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.25}>
          <div className="mt-10 grid grid-cols-3 gap-6 border-t border-navy-900/10 pt-8">
            {[
              ["1,000+", "Families Served"],
              ["₹500 Cr+", "Properties Sold"],
              ["4.9", "Google Rating"],
            ].map(([v, l]) => (
              <div key={l}>
                <p className="font-serif text-2xl font-medium text-navy-900 sm:text-3xl">{v}</p>
                <p className="mt-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-gray-400 sm:text-[11px]">{l}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const ContactBlock = () => (
  <section className="bg-cream py-24 sm:py-28" data-testid="contact-section">
    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 lg:grid-cols-2 lg:px-10">
      <div>
        <SectionHeading
          overline="Visit or Write"
          title={<>Our doors are <span className="italic text-gold-600">open</span></>}
          description="Walk in for a coffee and a candid market chat, or send the form — either way, a senior advisor responds today."
        />
        <Reveal delay={0.15}>
          <ul className="mt-10 space-y-6">
            <li className="flex gap-4 border border-navy-900/10 bg-white p-5" data-testid="contact-address-card">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center bg-navy-900"><MapPin className="h-5 w-5 text-gold-400" /></span>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-400">Office</p>
                <p className="mt-1.5 text-sm leading-relaxed text-navy-900">{BRAND.address}</p>
              </div>
            </li>
            <li className="flex gap-4 border border-navy-900/10 bg-white p-5" data-testid="contact-email-card">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center bg-navy-900"><Mail className="h-5 w-5 text-gold-400" /></span>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-400">Email</p>
                <a href={`mailto:${BRAND.email}`} className="mt-1.5 block text-sm text-navy-900 transition-colors duration-300 hover:text-gold-600">{BRAND.email}</a>
              </div>
            </li>
            <li className="flex gap-4 border border-navy-900/10 bg-white p-5" data-testid="contact-phone-card">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center bg-navy-900"><Phone className="h-5 w-5 text-gold-400" /></span>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-400">Phone / WhatsApp</p>
                <a href={`tel:+${BRAND.phoneRaw}`} className="mt-1.5 block text-sm text-navy-900 transition-colors duration-300 hover:text-gold-600">{BRAND.phone}</a>
              </div>
            </li>
            <li className="flex gap-4 border border-navy-900/10 bg-white p-5" data-testid="contact-hours-card">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center bg-navy-900"><Clock className="h-5 w-5 text-gold-400" /></span>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-400">Hours</p>
                <p className="mt-1.5 text-sm text-navy-900">Monday – Sunday, 10:00 AM – 7:00 PM</p>
              </div>
            </li>
            <li className="flex gap-4 border border-navy-900/10 bg-white p-5" data-testid="contact-instagram-card">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center bg-navy-900"><Instagram className="h-5 w-5 text-gold-400" /></span>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-400">Instagram</p>
                <a href={BRAND.instagram} target="_blank" rel="noopener noreferrer" className="mt-1.5 block text-sm text-navy-900 transition-colors duration-300 hover:text-gold-600" data-testid="contact-instagram-link">
                  {BRAND.instagramHandle} — follow us for new listings
                </a>
              </div>
            </li>
            <li className="flex gap-4 border border-navy-900/10 bg-white p-5" data-testid="contact-youtube-card">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center bg-navy-900"><Youtube className="h-5 w-5 text-gold-400" /></span>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-400">YouTube</p>
                <a href={BRAND.youtube} target="_blank" rel="noopener noreferrer" className="mt-1.5 block text-sm text-navy-900 transition-colors duration-300 hover:text-gold-600" data-testid="contact-youtube-link">
                  {BRAND.youtubeHandle} — watch project videos & tours
                </a>
              </div>
            </li>
          </ul>
        </Reveal>
      </div>
      <Reveal delay={0.15}>
        <div className="border border-navy-900/10 bg-white p-8 shadow-[0_30px_70px_rgb(13,27,62,0.1)] sm:p-10 lg:sticky lg:top-28">
          <p className="mb-2 font-serif text-2xl font-medium text-navy-900">Direct enquiry</p>
          <p className="mb-7 text-sm text-gray-500">Fields marked are enough — we'll take it from there.</p>
          <LeadForm tid="contact-form" source="Contact Page" submitLabel="Send Enquiry" showMessage />
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <PageHero
        overline="About & Contact"
        title="10 years of trust"
        accent="& transparency."
        description="Real estate services built on verified listings, written pricing and relationships that outlast the sale."
        image={IMG.trust}
      />
      <Story />
      <Testimonials />
      <ContactBlock />
    </div>
  );
}
